// src/pages/PaymentPage.tsx

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Heart, Gift, Lock, Sparkles, Loader2 } from "lucide-react";
import { PaymentModal } from "../components/payment/PaymentModal";
import { useCard } from "../hooks/useCard";
import { useAuth } from "../hooks/useAuth";
import { paymentCalculator } from "../services/payment-calculator.service";
import { PAYMENT_CONFIG, PAYMENT_ROUTES } from "../config/payment.config";
import toast from "react-hot-toast";
import logo from "../assets/img/logo.png";
import { PaystackProvider } from "../components/payment/PaystackProvider";
import { PaymentBreakdown as IPaymentBreakdown } from "../services/payment-calculator.service";
import { cardService } from "../services/card.service";
import { whitelistService } from "../services/whitelist.service";
import { tiktokPixelService } from "../services/tiktok-pixel.service";
import { useCampaign } from "../campaigns/providers/CampaignProvider";
import { PrivacyTrustNote } from "../components/legal/PrivacyTrustNote";

interface FeatureItem {
  icon: React.ElementType;
  label: string;
  value: string;
}

export default function PaymentPage() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { card, refreshCard } = useCard(cardId);
  const [showPayment, setShowPayment] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [breakdown, setBreakdown] = useState<IPaymentBreakdown | null>(null);
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(true);
  const [isFree, setIsFree] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { activeCampaign } = useCampaign();

  const paymentHeadline = activeCampaign?.copy.paymentHeadline || "Complete your payment";
  const paymentSubtitle =
    activeCampaign?.copy.paymentSubtitle || "Unlock your private LRNSY experience.";
  const paymentCTA = activeCampaign?.copy.paymentCTA || "Continue";

  // Track if we've already handled free redirect
  const freeRedirectHandledRef = useRef(false);

  // Check if card is paid whenever card changes
  useEffect(() => {
    if (card?.status === "paid") {
      console.log("🎯 Card is paid, setting isPaid to true");
      setIsPaid(true);
    }
  }, [card]);

  useEffect(() => {
    if (!user) {
      navigate("/signin");
    }
  }, [user, navigate]);

  // Load breakdown with discount and check if free
  useEffect(() => {
    const loadBreakdown = async () => {
      setIsLoadingBreakdown(true);
      try {
        console.log("📊 [PaymentPage] Loading breakdown for:", {
          cardId: card?.id,
          bouquetAmount: card?.bouquetAmount,
          userEmail: user?.email,
          includeBouquet: card?.includeBouquet,
        });

        const calculated = await paymentCalculator.calculatePaymentBreakdownWithDiscount(
          card?.bouquetAmount || 0,
          0,
          user?.email
        );

        console.log("📊 [PaymentPage] Breakdown loaded:", {
          total: calculated.total,
          isFree: calculated.total === 0,
          discountPercentage: calculated.discountPercentage,
          petals: calculated.petals,
          breakdown: calculated,
        });

        setBreakdown(calculated);
        setIsFree(calculated.total === 0);
      } catch (error) {
        console.error("❌ [PaymentPage] Error calculating breakdown:", error);
      } finally {
        setIsLoadingBreakdown(false);
      }
    };

    if (card) {
      loadBreakdown();
    }
  }, [card, user?.email]);

  useEffect(() => {
    if (!card || !breakdown) return;

    tiktokPixelService.trackViewContent(
      {
        content_id: card.id,
        content_type: "product",
        content_name: `${card.theme || "LRNSY"} Experience Payment`,
      },
      breakdown.total,
      "KES",
      `viewcontent:payment:${card.id}`
    );
  }, [card, breakdown]);

  useEffect(() => {
    if (!showPayment || !card || !breakdown) return;

    tiktokPixelService.trackInitiateCheckout(
      {
        content_id: card.id,
        content_type: "product",
        content_name: `${card.theme || "LRNSY"} Experience Checkout`,
      },
      breakdown.total,
      "KES",
      `initiatecheckout:paymentmodal:${card.id}`
    );
  }, [showPayment, card, breakdown]);

  // Handle free users - IMMEDIATE REDIRECT TO SHARE PAGE
  useEffect(() => {
    const handleFreeUser = async () => {
      // Only run once
      if (freeRedirectHandledRef.current) return;

      // Conditions must be met
      if (!card || !isFree || !user?.id || isPaid || isRedirecting) return;

      console.log("🎯 Free user detected, redirecting to share page");
      freeRedirectHandledRef.current = true;
      setIsRedirecting(true);

      try {
        // Mark card as paid in the background (don't wait for it)
        cardService.markCardAsPaid(cardId!, "free_access").catch(console.error);

        // Increment usage in the background
        whitelistService.incrementUsage(user.email).catch(console.error);

        // Show success toast
        toast.success("🎉 Welcome! Your gift is ready to share!");

        tiktokPixelService.track(
          "UnlockFreeAccess",
          {
            contents: [
              {
                content_id: card.id,
                content_type: "product",
                content_name: `${card.theme || "LRNSY"} Experience`,
              },
            ],
          },
          `free_access:${card.id}`
        );

        // Immediately navigate to share page
        setTimeout(() => {
          navigate(PAYMENT_ROUTES.SHARE_PAGE(cardId!));
        }, 500);
      } catch (error) {
        console.error("❌ Error handling free user:", error);
        setIsRedirecting(false);
        freeRedirectHandledRef.current = false; // Reset so user can try manual button
      }
    };

    handleFreeUser();
  }, [card, isFree, user, cardId, navigate, isPaid, isRedirecting]);

  // Safe access to breakdown properties with fallbacks
  const features: FeatureItem[] = useMemo(() => {
    const petalsCount = breakdown?.petals ?? 0;

    return [
      {
        icon: Gift,
        label: "Digital Bouquet",
        value: card?.includeBouquet
          ? paymentCalculator.formatPrice(card.bouquetAmount || 0)
          : "Not included",
      },
      {
        icon: Sparkles,
        label: "Petals",
        value: petalsCount > 0 ? `${petalsCount} petals` : "None",
      },
      { icon: Lock, label: "Private Link", value: "Shareable URL" },
      { icon: Heart, label: "Editable", value: "Until opened" },
    ];
  }, [card, breakdown]);

  const handlePaymentSuccess = useCallback(async () => {
    console.log("🎯 handlePaymentSuccess called - refreshing card");
    setIsProcessing(true);

    try {
      await refreshCard();

      if (card && breakdown) {
        tiktokPixelService.trackPurchase(
          {
            content_id: card.id,
            content_type: "product",
            content_name: `${card.theme || "LRNSY"} Experience`,
          },
          breakdown.total,
          "KES",
          `purchase:success:${card.id}`
        );
      }
    } catch (error) {
      console.error("Error after payment:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [refreshCard, card, breakdown]); // ✅ FIX

  // Show loading state while card is loading
  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Show breakdown loading state
  if (isLoadingBreakdown) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-surface pt-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <PaymentHeader recipientName={card.recipientName} />
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-text-tertiary">Calculating your payment...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show redirecting state
  if (isRedirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-surface pt-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <PaymentHeader recipientName={card.recipientName} />
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"
              />
              <p className="text-green-400">Preparing your gift...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isPaid) {
    console.log("🎯 Rendering PaymentSuccess for card:", cardId);
    return <PaymentSuccess cardId={cardId} recipientName={card.recipientName} />;
  }

  // If free but redirect didn't happen (fallback), show a simple button
  if (isFree) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-surface pt-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <PaymentHeader recipientName={card.recipientName} />
          <div className="flex justify-center items-center py-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-8 text-center max-w-md"
            >
              <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">You're a VIP! 🎉</h2>
              <p className="text-text-tertiary mb-6">
                You've received complimentary access to share your gift.
              </p>
              <button
                onClick={async () => {
                  setIsRedirecting(true);
                  try {
                    await cardService.markCardAsPaid(cardId!, "free_access");
                    await whitelistService.incrementUsage(user?.email!);
                    toast.success("🎉 Welcome! Your gift is ready to share!");
                    navigate(PAYMENT_ROUTES.SHARE_PAGE(cardId!));
                  } catch (error) {
                    console.error("Error:", error);
                    setIsRedirecting(false);
                  }
                }}
                disabled={isRedirecting}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50"
              >
                {isRedirecting ? "Redirecting..." : "Continue to Share"}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Regular payment flow for non-free users
  return (
    <PaystackProvider
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-white/60">Loading payment system...</p>
          </div>
        </div>
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-background to-surface pt-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <PaymentHeader recipientName={card.recipientName} />

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <PaymentPreview card={card} breakdown={breakdown} />

            <div className="md:col-span-2">
              <div className="bg-surface/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h2 className="text-xl font-bold text-text-primary mb-6">Payment Summary</h2>

                {breakdown && <PaymentBreakdown breakdown={breakdown} />}
                {!breakdown && (
                  <div className="py-4 text-center text-text-tertiary">
                    Loading payment details...
                  </div>
                )}

                <PaymentFeatures features={features} />

                <button
                  onClick={() => setShowPayment(true)}
                  disabled={isProcessing || !breakdown}
                  className="w-full py-5 bg-gradient-to-r from-primary to-primary-intense text-white rounded-2xl font-bold hover:shadow-xl hover:shadow-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                >
                  {isProcessing
                    ? "Processing..."
                    : breakdown
                      ? `Unlock for ${paymentCalculator.formatPrice(breakdown.total)}`
                      : "Loading..."}
                </button>

                <p className="text-center text-[10px] text-text-tertiary mt-4">
                  By completing this payment, you agree to our Terms of Service
                </p>
              </div>
            </div>
          </div>

          <PaymentModal
            isOpen={showPayment}
            onClose={() => setShowPayment(false)}
            cardId={cardId!}
            email={user?.email!}
            recipientName={card?.recipientName}
            bouquetAmount={card?.bouquetAmount}
            onSuccess={handlePaymentSuccess}
          />
        </div>
      </div>
    </PaystackProvider>
  );
}

// All sub-components remain exactly the same...
const PaymentPreview: React.FC<{ card: any; breakdown: IPaymentBreakdown | null }> = ({
  card,
  breakdown,
}) => {
  const navigate = useNavigate();
  const petalsCount = breakdown?.petals ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="md:col-span-1 cursor-pointer"
      onClick={() => navigate(`/preview/${card.id}`)}
    >
      <div className="bg-surface/50 backdrop-blur-sm border border-white/10 rounded-3xl p-6 sticky top-24">
        <h3 className="text-sm font-bold text-text-primary mb-4 flex items-center gap-2">
          <Gift size={16} className="text-primary" />
          Your Creation
        </h3>

        <div className="space-y-4">
          {card.recipientImage && (
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img src={card.recipientImage} alt="Preview" className="w-full h-full object-cover" />
            </div>
          )}

          <div className="space-y-2">
            <p className="text-xs text-text-tertiary">TO</p>
            <p className="font-black text-xl text-white">{card.recipientName}</p>
            <p className="text-sm text-text-tertiary">from {card.senderName}</p>
          </div>

          {card.includeBouquet && card.bouquetAmount && (
            <div className="bg-primary/10 rounded-xl p-3 border border-primary/20">
              <p className="text-[10px] text-primary/80">BOUQUET</p>
              <p className="text-lg font-black text-primary">
                {paymentCalculator.formatPrice(card.bouquetAmount)}
              </p>
              <p className="text-[8px] text-primary/60">
                {petalsCount > 0 ? `${petalsCount} petals` : "Calculating..."}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const PaymentBreakdown: React.FC<{ breakdown: IPaymentBreakdown }> = ({ breakdown }) => {
  return (
    <div className="space-y-4 mb-8">
      <div className="flex justify-between items-center py-2">
        <span className="text-text-tertiary">
          Experience Access (min. {PAYMENT_CONFIG.basePlatformFee} KES)
        </span>
        <span className="text-text-primary font-bold">
          {paymentCalculator.formatPrice(breakdown.totalPlatformFee)}
        </span>
      </div>

      {breakdown.extraContribution > 0 && (
        <div className="flex justify-between items-center py-2 text-primary/80">
          <span className="text-text-tertiary">Your extra support</span>
          <span className="text-text-primary font-bold">
            +{paymentCalculator.formatPrice(breakdown.extraContribution)}
          </span>
        </div>
      )}

      <div className="flex justify-between items-center py-2">
        <span className="text-text-tertiary">VAT ({PAYMENT_CONFIG.vatRate * 100}%)</span>
        <span className="text-text-primary font-bold">
          {paymentCalculator.formatPrice(breakdown.vat)}
        </span>
      </div>

      <div className="border-t border-white/10 pt-4 mt-4">
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold text-text-primary">Total</span>
          <span className="text-3xl md:text-4xl font-black text-primary">
            {paymentCalculator.formatPrice(breakdown.total)}
          </span>
        </div>
        <p className="text-[10px] text-text-tertiary text-right mt-1">One-time payment • Secure</p>
        <PrivacyTrustNote variant="payment" />
      </div>
    </div>
  );
};

const PaymentSuccess: React.FC<{ cardId?: string; recipientName: string }> = ({
  cardId,
  recipientName,
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-surface">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center max-w-md mx-auto p-8"
      >
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-24 h-24 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle size={48} className="text-success" />
        </motion.div>
        <h1 className="text-3xl font-black text-text-primary mb-2">Payment Successful!</h1>
        <p className="text-text-tertiary mb-8">Your card is ready to share with {recipientName}</p>
        <button
          onClick={() => navigate(PAYMENT_ROUTES.SHARE_PAGE(cardId!))}
          className="px-8 py-4 bg-primary text-white rounded-xl font-bold flex items-center gap-2 mx-auto hover:bg-primary-intense transition-all group"
        >
          Continue to Share
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </motion.div>
    </div>
  );
};

const PaymentHeader: React.FC<{ recipientName: string }> = ({ recipientName }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-center mb-12"
  >
    <div className="relative inline-block">
      <img src={logo} alt="LRNSY" className="w-16 h-16 mx-auto mb-6" />
    </div>
    <h1 className="text-4xl md:text-5xl font-black text-text-primary mb-4">
      Almost Ready to Share
    </h1>
    <p className="text-text-tertiary text-lg max-w-2xl mx-auto">
      Complete your payment to unlock the shareable link and deliver your love to {recipientName}
    </p>
  </motion.div>
);

const PaymentFeatures: React.FC<{ features: FeatureItem[] }> = ({ features }) => (
  <div className="grid grid-cols-2 gap-3 mb-8">
    {features.map((feature, index) => (
      <div
        key={index}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10"
      >
        <div className="flex items-center gap-2 mb-1">
          <feature.icon size={12} className="text-primary" />
          <span className="text-[9px] font-bold uppercase tracking-wider text-white/60">
            {feature.label}
          </span>
        </div>
        <span className="text-xs font-bold text-white">{feature.value}</span>
      </div>
    ))}
  </div>
);
